import { useState } from "react";
import { useNavigate } from "react-router-dom";

import CustomerInfoForm from "../../components/checkout/CustomerInfoForm";
import PaymentMethodSelector from "../../components/checkout/PaymentMethodSelector";
import ErrorModal from "../../components/modal/ErrorModal ";
import PaymentProcessingModal from "../../components/modal/PaymentProcessingModal";
import TransactionStatusModal from "../../components/modal/TransactionStatusModal";
import NeoProcessingOverlay from "./NeoProcessingOverlay";
import NeoStatusScreen from "./NeoStatusScreen";
import LanguageSelector from "../../components/LanguageSelector";

import { useCheckout } from "../../context/CheckoutContext";
import { useTranslation } from "../../hooks/useTranslation";
import { darkenColor } from "../../utils/commonUtils";
import { normalizeUrlProtocol } from "../../utils/redirectUrlBuilder";
import { SkinModalProvider, useSkinModals } from "../SkinModalContext";

import NeoOrderSummary from "./NeoOrderSummary";

import {
  NeoPageWrapper,
  NeoCard,
  NeoLeftPanel,
  NeoHeaderRow,
  NeoBackArrow,
  NeoLogoCircle,
  NeoMerchantName,
  NeoRightPanel,
  NeoTopRightControls,
  NeoSectionLabel,
  NeoContactSection,
  NeoPaymentSection,
  NeoPaymentSlot,
  NeoTermsBlock,
  NeoCTASection,
  NeoPayButton,
  NeoApplePaySlot,
  NeoFinePrint,
  NeoFooter,
  NeoCustomerFormSlot,
} from "./NeoStyles";

// Outer wrapper: mounts the SkinModalProvider so any descendant
// PaymentProcessingModal / TransactionStatusModal pushes through context
// instead of rendering its legacy UI.
const NeoPage = (props) => (
  <SkinModalProvider>
    <NeoPageContent {...props} />
  </SkinModalProvider>
);

const NeoPageContent = ({
  checkoutPageConfDto,
  checkoutPageFields,
  routingEntries,
  platformItemDetails,
  itemDetails,
  formData,
  isFormValid,
  billingPlatformId,
  planId,
  selectedAddons,
  sessionId,
  couponCodes,
  isLoading,
  isRoutingLoaded,
  hasNoPaymentMethods,
  showModal,
  transactionStatus,
  transactionId,
  isErrorModalVisible,
  isPaymentProcessingModalVisible,
  paymentProcessingHeader,
  paymentProcessingMessage,
  isTransactionDetailsModalVisible,
  handleFormChange,
  handleValidationChange,
  handleSubmit,
  setShowModal,
  setTransactionStatus,
  setErrorModalVisible,
  setIsPaymentProcessingModalVisible,
  setIsTransactionDetailsModalVisible,
  setCheckoutPageConfDto,
}) => {
  const { processing: ctxProcessing, status: ctxStatus, closeStatus } = useSkinModals();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { triggerPaymentEvent, paymentMethodName } = useCheckout();
  const [isProcessing, setIsProcessing] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const isApplePaySelected = paymentMethodName === "applepay";
  const brandColor = checkoutPageConfDto?.colour;
  const hoverColor = brandColor ? darkenColor(brandColor, 15) : undefined;

  const merchantName =
    checkoutPageConfDto?.companyName || checkoutPageConfDto?.brand || "";
  const termsAndConditionsUrl = checkoutPageConfDto?.termsAndConditionsUrl?.trim()
    ? normalizeUrlProtocol(checkoutPageConfDto.termsAndConditionsUrl)
    : "";

  const handleNeoSubmit = async () => {
    if (!isFormValid || hasNoPaymentMethods || isProcessing || !termsAccepted) return;
    setIsProcessing(true);
    try {
      await triggerPaymentEvent?.();
      await handleSubmit?.();
    } catch (err) {
      console.error("Neo payment failed:", err);
    } finally {
      setIsProcessing(false);
    }
  };

  const ctaDisabled =
    !isFormValid || hasNoPaymentMethods || isProcessing || !termsAccepted;

  return (
    <>
      <NeoPageWrapper>
        <NeoCard>
          {/* ── LEFT PANEL: order summary ── */}
          <NeoLeftPanel>
            <NeoHeaderRow>
              <NeoBackArrow
                type="button"
                onClick={() => navigate(-1)}
                aria-label="Go back"
              >
                ←
              </NeoBackArrow>

              <NeoLogoCircle
                $color={brandColor}
                $hasImage={!!checkoutPageConfDto?.logoUrl}
              >
                {checkoutPageConfDto?.logoUrl && (
                  <img
                    src={checkoutPageConfDto.logoUrl}
                    alt={merchantName || "Logo"}
                  />
                )}
              </NeoLogoCircle>

              <NeoMerchantName>{merchantName}</NeoMerchantName>
            </NeoHeaderRow>

            <NeoOrderSummary
              itemDetails={itemDetails}
              platformItemDetails={platformItemDetails}
              selectedAddons={selectedAddons}
              billingConnectionId={billingPlatformId}
              planId={planId}
              formData={formData}
              presetCouponCodes={couponCodes}
              checkoutPageConfDto={checkoutPageConfDto}
              checkoutPageFields={checkoutPageFields}
              sessionId={sessionId}
            />
          </NeoLeftPanel>

          {/* ── RIGHT PANEL: contact + payment + CTA ── */}
          <NeoRightPanel>
            <NeoTopRightControls>
              <LanguageSelector />
            </NeoTopRightControls>

            {/* Contact information + all configured billing/shipping fields */}
            <NeoContactSection>
              <NeoSectionLabel>
                {t("checkout.neo.contactInformation")}
              </NeoSectionLabel>
              <NeoCustomerFormSlot>
                <CustomerInfoForm
                  formData={formData}
                  onChange={handleFormChange}
                  checkoutPageFields={checkoutPageFields}
                  formValidations={handleValidationChange}
                  itemDetails={itemDetails}
                  disabled={hasNoPaymentMethods}
                />
              </NeoCustomerFormSlot>
            </NeoContactSection>

            {/* Payment method */}
            <NeoPaymentSection>
              <NeoPaymentSlot>
                <PaymentMethodSelector
                  selectedMethod={formData.paymentMethod}
                  routingEntries={routingEntries}
                  onChange={(method) =>
                    handleFormChange({ paymentMethod: method })
                  }
                  itemDetails={itemDetails}
                  formData={formData}
                  billingPlatformId={billingPlatformId}
                  billingPlatformName={itemDetails?.billingPlatformName}
                  planId={planId}
                  platformItemDetails={platformItemDetails}
                  selectedAddons={selectedAddons}
                  checkoutPageConfDto={checkoutPageConfDto}
                  setCheckoutPageConfDto={setCheckoutPageConfDto}
                  isLoading={isLoading}
                  isRoutingLoaded={isRoutingLoaded}
                  sessionId={sessionId}
                  compact
                  silentDrawerMethods={["applepay"]}
                />
              </NeoPaymentSlot>
            </NeoPaymentSection>

            {/* Terms consent */}
            <NeoTermsBlock>
              <input
                type="checkbox"
                id="neo-terms"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
              />
              <label htmlFor="neo-terms">
                {t("checkout.neo.consentTextStart")}
                {termsAndConditionsUrl ? (
                  <a href={termsAndConditionsUrl} target="_blank" rel="noreferrer">
                    {t("checkout.neo.consentTextLink")}
                  </a>
                ) : (
                  <span>{t("checkout.neo.consentTextLink")}</span>
                )}
                {t("checkout.neo.consentTextEnd")}
              </label>
            </NeoTermsBlock>

            {/* CTA */}
            <NeoCTASection>
              {isApplePaySelected ? (
                <NeoApplePaySlot
                  id="altruon-applepay-slot"
                  $disabled={ctaDisabled}
                  aria-disabled={ctaDisabled}
                />
              ) : (
                <NeoPayButton
                  $color={brandColor}
                  $hoverColor={hoverColor}
                  disabled={ctaDisabled}
                  onClick={handleNeoSubmit}
                  type="button"
                >
                  {isProcessing
                    ? t("checkout.neo.processing")
                    : t("checkout.neo.payAndSubscribe")}
                </NeoPayButton>
              )}

              <NeoFinePrint>
                {t("checkout.neo.authorizeText", {
                  variables: { merchant: merchantName || t("common.merchant") || "the merchant" },
                })}
              </NeoFinePrint>

              <NeoFooter>
                <span>{t("checkout.neo.poweredBy")}</span>
                <a href="/terms" target="_blank" rel="noreferrer">
                  {t("checkout.neo.termsLink")}
                </a>
                <a href="/privacy" target="_blank" rel="noreferrer">
                  {t("checkout.neo.privacyLink")}
                </a>
              </NeoFooter>
            </NeoCTASection>
          </NeoRightPanel>
        </NeoCard>
      </NeoPageWrapper>

      {/*
        Bridge instances of the legacy modals — they render NOTHING because the
        SkinModalProvider above intercepts them, but their hooks (WebSocket,
        countdown, etc.) still execute and push state into context.
      */}
      <PaymentProcessingModal
        show={isPaymentProcessingModalVisible}
        setShow={setIsPaymentProcessingModalVisible}
        heading={paymentProcessingHeader}
        content={paymentProcessingMessage}
        transactionId={transactionId}
        setTransactionStatus={setTransactionStatus}
        setTransactionStatusModal={setShowModal}
      />
      <TransactionStatusModal
        show={showModal}
        onClose={() => setShowModal(false)}
        status={transactionStatus}
        transactionId={transactionId}
        hostedPageRedirect={null}
        fallbackRedirectUrl={checkoutPageConfDto?.checkoutRedirectUrl}
      />

      {/* Neo overlays — driven by skin-modal context (any source) */}
      <NeoProcessingOverlay
        show={!!ctxProcessing}
        heading={ctxProcessing?.heading}
        content={ctxProcessing?.content}
        transactionId={ctxProcessing?.transactionId}
      />
      <NeoStatusScreen
        show={!!ctxStatus}
        onClose={() => {
          ctxStatus?.onClose?.();
          closeStatus?.();
        }}
        status={ctxStatus?.status}
        transactionId={ctxStatus?.transactionId}
        hostedPageRedirect={ctxStatus?.hostedPageRedirect}
        fallbackRedirectUrl={
          ctxStatus?.fallbackRedirectUrl || checkoutPageConfDto?.checkoutRedirectUrl
        }
        redirectUrl={ctxStatus?.redirectUrl}
        merchantName={merchantName}
        brandColor={brandColor}
        logoUrl={checkoutPageConfDto?.logoUrl}
      />

      <ErrorModal
        show={isErrorModalVisible}
        onClose={() => setErrorModalVisible(false)}
        message="This plan may no longer be available in its current form. Please contact the merchant for assistance."
        redirectUrl="/"
        buttonText="Go Back"
        variant="danger"
        icon="bi-exclamation-triangle-fill"
        size="lg"
        centered
        animation="fade"
        showCountdown={false}
      />
    </>
  );
};

export default NeoPage;
